import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ShareButtons from '../components/ShareButtons';
import NewsletterSignup from '../components/NewsletterSignup';
import { useAuthStore } from '../store/authStore';
import { Bell, Lock, TrendingUp, TrendingDown, X } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'http://64.23.240.10/api/v1';
const BASE_API = API.replace('/v1', '');

interface Protocol {
  id: number;
  name: string;
  slug: string;
  chain: string;
  category: string;
  current_apy: string;
  token: string;
  high_apy: number | null;
  low_apy: number | null;
  avg_apy: string | null;
  history: { apy: string; fetched_at: string }[];
  gated?: boolean;
  gatedFields?: string[];
  message?: string;
  plan?: string;
  risk_level?: string;
}

export default function ProtocolPage() {
  const { slug } = useParams<{ slug: string }>();
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(1000);
  const [related, setRelated] = useState<any[]>([]);
  const { isAuthenticated, token, user } = useAuthStore();

  const [intelligence, setIntelligence] = useState<any>(null);

  const [alertDirection, setAlertDirection] = useState<'below' | 'above'>('below');
  const [alertThreshold, setAlertThreshold] = useState('');
  const [alertLoading, setAlertLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlertLimitPrompt, setShowAlertLimitPrompt] = useState(false);

  useEffect(() => {
    fetch(`${BASE_API}/protocols/${slug}/related`)
      .then(r => r.json())
      .then(d => setRelated(d.data || []));
  }, [slug]);

  useEffect(() => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`${BASE_API}/protocols/${slug}/detail`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          fetch(`${API}/staking/options/${slug}`)
            .then(r => r.json())
            .then(d => { setProtocol(d.data?.option || null); setLoading(false); })
            .catch(() => setLoading(false));
        } else {
          setProtocol(data);
          if (data.intelligence) setIntelligence(data.intelligence);
          setLoading(false);
        }
      })
      .catch(() => {
        fetch(`${API}/staking/options/${slug}`)
          .then(r => r.json())
          .then(d => { setProtocol(d.data?.option || null); setLoading(false); })
          .catch(() => setLoading(false));
      });
  }, [slug, token]);

  const handleSetAlert = async () => {
    if (!isAuthenticated) return;
    if (!alertThreshold || !protocol) return;
    setAlertLoading(true);
    setAlertMessage('');
    setShowAlertLimitPrompt(false);
    try {
      const res = await fetch(`${BASE_API}/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ protocol_id: protocol.id, direction: alertDirection, threshold: parseFloat(alertThreshold) })
      });
      const data = await res.json();
      if (res.ok) {
        setAlertMessage('Alert set! We\'ll email you when APY changes.');
        setAlertThreshold('');
      } else {
        const errMsg = data.error || 'Failed to set alert';
        if (errMsg.toLowerCase().includes('limit') || errMsg.toLowerCase().includes('upgrade') || res.status === 403) {
          setShowAlertLimitPrompt(true);
        }
        setAlertMessage(errMsg);
      }
    } catch {
      setAlertMessage('Failed to set alert');
    }
    setAlertLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-navy-dark flex items-center justify-center">
      <div className="animate-spin w-12 h-12 border-4 border-neon-red border-t-transparent rounded-full"/>
    </div>
  );

  if (!protocol) return (
    <div className="min-h-screen bg-navy-dark flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-cream mb-4">Protocol not found</h1>
        <Link to="/staking" className="text-sky-blue hover:text-neon-red">← Back to all protocols</Link>
      </div>
    </div>
  );

  const apy = parseFloat(protocol.current_apy || '0');
  const yearly = (amount * apy) / 100;
  const monthly = yearly / 12;
  const daily = yearly / 365;

  const categoryLabel: Record<string, string> = {
    'native-staking': 'Native Staking',
    'liquid-staking': 'Liquid Staking',
    'defi-yield': 'DeFi Yield'
  };

  const riskLevelMap: Record<string, { label: string; color: string }> = {
    'low':    { label: 'Low Risk', color: 'text-green-400' },
    'medium': { label: 'Medium Risk', color: 'text-yellow-400' },
    'high':   { label: 'Higher Risk', color: 'text-neon-red' }
  };
  const categoryFallback: Record<string, string> = {
    'native-staking': 'low', 'liquid-staking': 'medium', 'defi-yield': 'high'
  };
  const rl = (protocol.risk_level || categoryFallback[protocol.category] || 'medium').toLowerCase();
  const risk = riskLevelMap[rl] || { label: 'Medium Risk', color: 'text-yellow-400' };
  const isGated = protocol.gated === true;
  const userTier = user?.subscriptionTier || 'free';
  const isPro = userTier === 'pro' || userTier === 'premium';
  const isStarterPlus = userTier === 'starter' || isPro;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: protocol.name,
    description: `Stake ${protocol.chain} with ${protocol.name} and earn ${apy.toFixed(2)}% APY. Compare with 156+ other staking protocols on ResidualVault.`,
    url: `https://www.residualvault.com/protocol/${protocol.slug}`,
    provider: { '@type': 'Organization', name: 'ResidualVault', url: 'https://www.residualvault.com' },
    annualPercentageRate: apy.toFixed(2),
    feesAndCommissionsSpecification: 'No platform fees',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.residualvault.com' },
        { '@type': 'ListItem', position: 2, name: 'Staking', item: 'https://www.residualvault.com/staking' },
        { '@type': 'ListItem', position: 3, name: protocol.name, item: `https://www.residualvault.com/protocol/${protocol.slug}` },
      ]
    }
  };

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <SEO
        title={`${protocol.name} Staking — ${apy.toFixed(2)}% APY | ResidualVault`}
        description={`Stake ${protocol.chain} with ${protocol.name} and earn ${apy.toFixed(2)}% APY. Compare ${protocol.name} staking rewards with 156+ other protocols on ResidualVault.`}
        canonical={`https://www.residualvault.com/protocol/${protocol.slug}`}
        keywords={`${protocol.name} staking, ${protocol.chain} staking APY, ${protocol.name} rewards, stake ${protocol.chain}`}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-4">
          <ShareButtons title={`${protocol.name} Staking — ${apy.toFixed(2)}% APY | ResidualVault`} url={`/protocol/${protocol.slug}`} />
        </div>

        <div className="mb-8">
          <Link to="/staking" className="text-sky-blue hover:text-neon-red transition-colors text-sm">
            ← Back to all protocols
          </Link>
        </div>

        {/* Hero */}
        <div className="card-premium mb-8">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 bg-gradient-cta rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-neon">
                {protocol.name[0]}
              </div>
              <div>
                <h1 className="text-4xl font-bold text-cream mb-2">{protocol.name}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1 bg-steel-blue/20 border border-sky-blue/30 rounded-full text-sky-blue text-sm font-semibold">{protocol.chain}</span>
                  <span className="px-3 py-1 bg-steel-blue/20 border border-sky-blue/30 rounded-full text-sky-blue text-sm">{categoryLabel[protocol.category] || protocol.category}</span>
                  <span className={`text-sm font-semibold ${risk.color}`}>{risk.label}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-6xl font-bold gradient-text-neon">{apy.toFixed(2)}%</div>
              <div className="text-sky-blue text-sm mt-1">Current APY</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Current APY', value: `${apy.toFixed(2)}%`, highlight: true, locked: false },
            { label: '24h High', value: protocol.high_apy != null ? `${parseFloat(String(protocol.high_apy)).toFixed(2)}%` : null, highlight: false, locked: isGated },
            { label: '24h Low', value: protocol.low_apy != null ? `${parseFloat(String(protocol.low_apy)).toFixed(2)}%` : null, highlight: false, locked: isGated },
            { label: 'Average APY', value: protocol.avg_apy != null ? `${parseFloat(protocol.avg_apy).toFixed(2)}%` : null, highlight: false, locked: isGated },
          ].map(s => (
            <div key={s.label} className="card-glass text-center relative">
              <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">{s.label}</div>
              {s.locked ? (
                <div>
                  <div className="text-3xl font-bold text-steel-blue/40 blur-sm select-none">12.34%</div>
                  <Link to="/register" className="absolute inset-0 flex items-center justify-center bg-navy-dark/60 rounded-xl">
                    <span className="flex items-center gap-1 text-neon-red text-sm font-semibold"><Lock className="w-4 h-4" /> Sign up free</span>
                  </Link>
                </div>
              ) : (
                <div className={`text-3xl font-bold ${s.highlight ? 'gradient-text-neon' : 'text-cream'}`}>{s.value}</div>
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">

          {/* Calculator */}
          <div className="card-premium">
            <h2 className="text-2xl font-bold text-cream mb-6">Staking calculator</h2>
            <div className="mb-6">
              <label className="text-sky-blue text-sm block mb-2">Amount to stake (USD)</label>
              <div className="flex items-center gap-2">
                <span className="text-sky-blue text-lg">$</span>
                <input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} className="flex-1 bg-navy-dark border border-sky-blue/30 rounded-lg px-4 py-3 text-cream text-lg focus:outline-none focus:border-neon-red transition-colors" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: 'Daily', value: daily },
                { label: 'Monthly', value: monthly },
                { label: 'Yearly', value: yearly },
              ].map(r => (
                <div key={r.label} className="bg-navy-dark/50 rounded-xl p-4 text-center border border-sky-blue/10">
                  <div className="text-sky-blue text-xs mb-2">{r.label}</div>
                  <div className="text-2xl font-bold gradient-text-neon">${r.value.toFixed(2)}</div>
                </div>
              ))}
            </div>
            <Link to="/register" className="btn-neon block text-center">
              Start staking {protocol.name} →
            </Link>
          </div>

          {/* APY History */}
          <div className="card-premium">
            <h2 className="text-2xl font-bold text-cream mb-6">Recent APY history</h2>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(protocol.history || []).slice(0, isGated ? 3 : 12).map((h, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-navy-dark/50 rounded-lg border border-sky-blue/10">
                  <span className="text-sky-blue text-sm">
                    {new Date(h.fetched_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="gradient-text-neon font-bold">{parseFloat(h.apy).toFixed(2)}%</span>
                </div>
              ))}
            </div>
            {isGated && (
              <Link to="/register" className="mt-4 flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-lg text-neon-red font-semibold hover:border-neon-red/60 transition-all">
                <Lock className="w-4 h-4" /> Sign up free to see full APY history
              </Link>
            )}
            {!isGated && protocol.plan === 'free' && (protocol.history || []).length >= 12 && (
              <Link to="/pricing#starter" className="mt-4 flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-lg text-neon-red font-semibold hover:border-neon-red/60 transition-all">
                <TrendingUp className="w-4 h-4" /> Upgrade to Starter for 48-point APY history — $4.99/mo
              </Link>
            )}
          </div>
        </div>

        {/* APY Alert */}
        <div className="card-premium mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-6 h-6 text-neon-red" />
            <h2 className="text-2xl font-bold text-cream">Set APY Alert</h2>
          </div>
          {!isAuthenticated ? (
            <div className="text-center py-6">
              <p className="text-sky-blue mb-4">Get notified by email when {protocol.name}'s APY changes.</p>
              <Link to="/register" className="btn-neon inline-block">Create free account to set alerts</Link>
            </div>
          ) : (
            <div>
              <p className="text-sky-blue mb-4">Get an email when {protocol.name}'s APY hits your target.</p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-sky-blue text-xs block mb-1">Alert me when APY</label>
                  <div className="flex gap-2">
                    <button onClick={() => setAlertDirection('below')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${alertDirection === 'below' ? 'bg-gradient-cta text-white' : 'bg-steel-blue/20 text-sky-blue border border-sky-blue/30'}`}>
                      <TrendingDown className="w-4 h-4" /> Drops below
                    </button>
                    <button onClick={() => setAlertDirection('above')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${alertDirection === 'above' ? 'bg-gradient-cta text-white' : 'bg-steel-blue/20 text-sky-blue border border-sky-blue/30'}`}>
                      <TrendingUp className="w-4 h-4" /> Rises above
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sky-blue text-xs block mb-1">APY threshold (%)</label>
                  <input type="number" step="0.01" placeholder={apy.toFixed(2)} value={alertThreshold} onChange={e => setAlertThreshold(e.target.value)} className="w-32 bg-navy-dark border border-sky-blue/30 rounded-lg px-4 py-2 text-cream focus:outline-none focus:border-neon-red transition-colors" />
                </div>
                <button onClick={handleSetAlert} disabled={alertLoading || !alertThreshold} className="btn-neon px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                  {alertLoading ? 'Setting...' : 'Set Alert'}
                </button>
              </div>

              {/* Alert limit prompt for free users */}
              {showAlertLimitPrompt && user?.subscriptionTier === 'free' && (
                <div className="mt-4 p-4 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-xl relative">
                  <button
                    onClick={() => setShowAlertLimitPrompt(false)}
                    className="absolute top-3 right-3 text-sky-blue/50 hover:text-cream transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <h3 className="text-base font-bold text-cream mb-2">
                    You've used your 1 free alert. Upgrade to Starter ($4.99/mo) for unlimited APY alerts across all 156 protocols.
                  </h3>
                  <Link
                    to="/pricing#starter"
                    className="btn-neon inline-flex items-center gap-2 px-5 py-2 text-sm mt-1"
                  >
                    Upgrade to Starter
                  </Link>
                </div>
              )}

              {alertMessage && !showAlertLimitPrompt && (
                <p className={`mt-3 text-sm ${alertMessage.includes('Upgrade') || alertMessage.includes('Failed') || alertMessage.includes('limit') ? 'text-neon-red' : 'text-green-400'}`}>
                  {alertMessage}
                </p>
              )}
              {user?.subscriptionTier === 'free' && !showAlertLimitPrompt && (
                <p className="mt-2 text-sky-blue/60 text-xs">Free plan: 1 alert. <Link to="/pricing#starter" className="text-neon-red hover:underline">Upgrade to Starter for unlimited alerts — $4.99/mo</Link></p>
              )}
            </div>
          )}
        </div>

        {/* Protocol Intelligence */}
        {(intelligence || isAuthenticated) && (
          <div className="card-premium mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-cta rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
              </div>
              <h2 className="text-2xl font-bold text-cream">Protocol Intelligence</h2>
            </div>

            {/* Grade and Tier - always visible for auth */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {isAuthenticated ? (
                <>
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 text-center">
                    <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Overall Grade</div>
                    <div className={`text-4xl font-bold ${
                      intelligence?.grade?.charAt(0) === 'A' ? 'text-green-400' :
                      intelligence?.grade?.charAt(0) === 'B' ? 'text-blue-400' :
                      intelligence?.grade?.charAt(0) === 'C' ? 'text-yellow-400' :
                      'text-orange-400'
                    }`}>{intelligence?.grade || '-'}</div>
                  </div>
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 text-center">
                    <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">TVL Tier</div>
                    <div className={`inline-block px-3 py-1 rounded-lg text-sm font-bold border ${
                      (intelligence?.tvl_tier || intelligence?.tvlTier || '') === 'Tier 1' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      (intelligence?.tvl_tier || intelligence?.tvlTier || '') === 'Tier 2' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      (intelligence?.tvl_tier || intelligence?.tvlTier || '') === 'Tier 3' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                      'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>{intelligence?.tvl_tier || intelligence?.tvlTier || '-'} {intelligence?.tvl_tier_label || intelligence?.tvlTierLabel ? `\u2014 ${intelligence.tvl_tier_label || intelligence.tvlTierLabel}` : ''}</div>
                  </div>
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 text-center">
                    <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Risk</div>
                    <div className={`text-lg font-bold ${
                      (intelligence?.risk_classification || '').toLowerCase().includes('low') ? 'text-green-400' :
                      (intelligence?.risk_classification || '').toLowerCase().includes('moderate') ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>{intelligence?.risk_classification || intelligence?.riskClassification || '-'}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 text-center relative">
                    <div className="blur-sm select-none">
                      <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Overall Grade</div>
                      <div className="text-4xl font-bold text-green-400">B+</div>
                    </div>
                    <Link to="/register" className="absolute inset-0 flex items-center justify-center bg-navy-dark/60 rounded-xl">
                      <span className="flex items-center gap-1 text-neon-red text-sm font-semibold">Sign up free</span>
                    </Link>
                  </div>
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 text-center relative">
                    <div className="blur-sm select-none">
                      <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">TVL Tier</div>
                      <div className="text-lg font-bold text-blue-400">Tier 1 \u2014 Established</div>
                    </div>
                    <Link to="/register" className="absolute inset-0 flex items-center justify-center bg-navy-dark/60 rounded-xl">
                      <span className="flex items-center gap-1 text-neon-red text-sm font-semibold">Sign up free</span>
                    </Link>
                  </div>
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 text-center relative">
                    <div className="blur-sm select-none">
                      <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Risk</div>
                      <div className="text-lg font-bold text-yellow-400">Moderate</div>
                    </div>
                    <Link to="/register" className="absolute inset-0 flex items-center justify-center bg-navy-dark/60 rounded-xl">
                      <span className="flex items-center gap-1 text-neon-red text-sm font-semibold">Sign up free</span>
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Detailed scores - gated by tier */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Stability Score - starter+ */}
              <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 relative">
                <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">APY Stability</div>
                {isStarterPlus && intelligence ? (
                  <div>
                    <div className="text-2xl font-bold text-cream mb-1">{intelligence.stability_score ?? intelligence.stabilityScore ?? '-'}<span className="text-sm text-sky-blue">/100</span></div>
                    <div className="h-2 bg-navy-dark rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${(intelligence.stability_score ?? 0) >= 70 ? 'bg-green-400' : (intelligence.stability_score ?? 0) >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${Math.min(100, intelligence.stability_score ?? intelligence.stabilityScore ?? 0)}%` }} />
                    </div>
                    <div className="text-xs text-sky-blue mt-1">{intelligence.stability_pattern || intelligence.stabilityPattern || ''}</div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="blur-sm select-none"><div className="text-2xl font-bold text-cream">78<span className="text-sm text-sky-blue">/100</span></div></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Link to={isAuthenticated ? "/pricing#starter" : "/register"} className="text-xs text-neon-red font-medium">{isAuthenticated ? 'Upgrade to Starter' : 'Sign up free'}</Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Risk Score - pro+ */}
              <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 relative">
                <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Risk Score</div>
                {isPro && intelligence ? (
                  <div className={`text-2xl font-bold ${
                    (intelligence.risk_score ?? 0) >= 75 ? 'text-green-400' :
                    (intelligence.risk_score ?? 0) >= 50 ? 'text-yellow-400' : 'text-red-400'
                  }`}>{intelligence.risk_score ?? intelligence.riskScore ?? '-'}<span className="text-sm text-sky-blue">/100</span></div>
                ) : (
                  <div className="relative">
                    <div className="blur-sm select-none"><div className="text-2xl font-bold text-yellow-400">65<span className="text-sm text-sky-blue">/100</span></div></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Link to="/pricing#pro" className="text-xs text-neon-red font-medium">Upgrade to Pro</Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Volatility-Adjusted Yield - pro+ */}
              <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 relative">
                <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Adj. Yield</div>
                {isPro && intelligence ? (
                  <div className="text-2xl font-bold gradient-text-neon">{intelligence.volatility_adjusted_yield ?? intelligence.adjustedYield ?? '-'}%</div>
                ) : (
                  <div className="relative">
                    <div className="blur-sm select-none"><div className="text-2xl font-bold gradient-text-neon">2.1%</div></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Link to="/pricing#pro" className="text-xs text-neon-red font-medium">Upgrade to Pro</Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Sweet Spot Score - pro+ */}
              <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 relative">
                <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">Sweet Spot</div>
                {isPro && intelligence ? (
                  <div className="text-2xl font-bold text-cream">{intelligence.sweet_spot_score ?? intelligence.sweetSpotScore ?? '-'}<span className="text-sm text-sky-blue">/100</span></div>
                ) : (
                  <div className="relative">
                    <div className="blur-sm select-none"><div className="text-2xl font-bold text-cream">45<span className="text-sm text-sky-blue">/100</span></div></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Link to="/pricing#pro" className="text-xs text-neon-red font-medium">Upgrade to Pro</Link>
                    </div>
                  </div>
                )}
              </div>

              {/* TVL Velocity - pro+ */}
              <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10 relative">
                <div className="text-sky-blue text-xs uppercase tracking-wider mb-2">TVL Velocity</div>
                {isPro && intelligence ? (
                  <div>
                    <div className={`text-lg font-bold ${
                      (intelligence.tvl_velocity || intelligence.tvlVelocity || '').toLowerCase().includes('growing') ? 'text-green-400' :
                      (intelligence.tvl_velocity || intelligence.tvlVelocity || '').toLowerCase().includes('declining') ? 'text-red-400' : 'text-yellow-400'
                    }`}>{intelligence.tvl_velocity || intelligence.tvlVelocity || '-'}</div>
                    {intelligence.tvl_change_7d != null && <div className="text-xs text-sky-blue">{intelligence.tvl_change_7d > 0 ? '+' : ''}{intelligence.tvl_change_7d}% 7d</div>}
                  </div>
                ) : (
                  <div className="relative">
                    <div className="blur-sm select-none"><div className="text-lg font-bold text-green-400">Growing</div><div className="text-xs text-sky-blue">+12.3% 7d</div></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Link to="/pricing#pro" className="text-xs text-neon-red font-medium">Upgrade to Pro</Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Upgrade banner if not pro */}
            {isAuthenticated && !isPro && (
              <div className="mt-6 p-4 bg-gradient-to-r from-neon-red/10 to-electric-blue/10 border border-neon-red/30 rounded-xl text-center">
                <p className="text-sky-blue text-sm mb-2">
                  {userTier === 'free' ? 'Upgrade to Starter ($4.99/mo) for stability scoring, or Pro ($9.99/mo) for full intelligence.' : 'Upgrade to Pro ($9.99/mo) to unlock risk scoring, adjusted yields, and sweet spot analysis.'}
                </p>
                <Link to="/pricing" className="btn-neon inline-block text-sm px-4 py-2">View Plans</Link>
              </div>
            )}
          </div>
        )}

        {/* About */}
        <div className="card-premium mb-8">
          <h2 className="text-2xl font-bold text-cream mb-4">About {protocol.name} staking</h2>
          <p className="text-sky-blue leading-relaxed mb-4">
            {protocol.name} ({protocol.chain}) is a {categoryLabel[protocol.category]?.toLowerCase() || 'staking'} protocol
            currently offering {apy.toFixed(2)}% APY. Staking {protocol.chain} allows you to earn passive income
            while contributing to network security and decentralization.
          </p>
          <p className="text-sky-blue leading-relaxed">
            With ${amount.toLocaleString()} staked at the current rate, you would earn approximately{' '}
            <span className="text-cream font-semibold">${yearly.toFixed(2)} per year</span>,{' '}
            <span className="text-cream font-semibold">${monthly.toFixed(2)} per month</span>, or{' '}
            <span className="text-cream font-semibold">${daily.toFixed(2)} per day</span>.
            Compare {protocol.name} with 156+ other protocols on ResidualVault to find the best staking rewards for your portfolio.
          </p>
        </div>

        {/* Related Protocols */}
        {related.length > 0 && (
          <div className="card-premium mb-8">
            <h2 className="text-2xl font-bold text-cream mb-6">Related Protocols</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(r => {
                const rapy = parseFloat(r.apy || 0);
                return (
                  <Link key={r.id} to={`/protocol/${r.slug}`} className="bg-navy-dark/50 border border-sky-blue/10 rounded-xl p-4 text-center hover:border-neon-red/30 hover:bg-steel-blue/10 transition-all group">
                    <div className="w-10 h-10 bg-gradient-cta rounded-xl flex items-center justify-center text-white font-bold mx-auto mb-3 group-hover:shadow-neon transition-all">{r.name[0]}</div>
                    <div className="text-cream font-semibold text-sm mb-1">{r.name}</div>
                    <div className="text-sky-blue text-xs mb-2">{r.chain}</div>
                    <div className="gradient-text-neon font-bold text-xl">{rapy.toFixed(2)}%</div>
                    <div className="text-sky-blue/60 text-xs">APY</div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Newsletter */}
        <div className="mb-8">
          <NewsletterSignup source={`protocol-${protocol.slug}`} />
        </div>

        {/* CTA */}
        <div className="card-premium text-center">
          <h2 className="text-3xl font-bold mb-4">
            <span className="text-cream">Compare </span>
            <span className="gradient-text-neon">156+ protocols</span>
          </h2>
          <p className="text-sky-blue mb-6">Find the best staking rewards across all major chains</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/staking" className="btn-neon inline-flex items-center">View all protocols →</Link>
            <Link to="/calculator" className="bg-steel-blue/20 border border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all inline-flex items-center">Staking calculator</Link>
            {!isAuthenticated && (
              <Link to="/register" className="bg-gradient-to-r from-neon-red to-electric-blue text-white font-semibold px-6 py-3 rounded-lg hover:shadow-neon transition-all inline-flex items-center">Create free account</Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
